import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { Avatar } from "@/components/common";

export function QuestionCard({ q: initial }) {
  const [q, setQ] = useState(initial);
  const { requireAuth } = useApp();
  const total = q.options.reduce((s, o) => s + o.votes, 0);
  const vote = requireAuth(async (oid) => {
    try { setQ((await api.post(`/questions/${q.id}/vote`, { option_id: oid })).data); toast.success("Thanks for voting 🙏"); } catch (e) { toast.error(errMsg(e)); }
  });
  const share = () => { navigator.clipboard?.writeText(`${window.location.origin}/questions/${q.id}`); toast.success("Question link copied"); };
  return (
    <div data-testid={`question-card-${q.id}`} className="card overflow-hidden">
      <div className="relative h-28"><img src={q.image} alt="" loading="lazy" className="h-full w-full object-cover" /><div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <p className="font-display absolute bottom-3 left-4 right-4 text-lg font-semibold leading-snug text-white">{q.question}</p></div>
      <div className="space-y-2 p-4">
        {q.options.map((o) => {
          const p = total ? Math.round((o.votes / total) * 100) : 0;
          const mine = q.my_vote === o.id;
          return (
            <button key={o.id} disabled={!!q.my_vote} onClick={() => vote(o.id)} data-testid={`vote-option-${o.id}`}
              className={`relative w-full overflow-hidden rounded-xl border px-3 py-2 text-left text-sm ${mine ? "border-[#6B1D2F]" : "border-[#E7E0D3]"} hover:border-[#D97706]`}>
              {q.my_vote && <span className="absolute inset-y-0 left-0 bg-[#FEF3C7]" style={{ width: `${p}%` }} />}
              <span className="relative flex justify-between"><span>{o.text}</span>{q.my_vote && <b>{p}%</b>}</span>
            </button>
          );
        })}
        <div className="flex items-center gap-4 pt-1 text-xs text-[#78716C]">
          <span>{total.toLocaleString("en-IN")} votes</span>
          <Link to={`/questions/${q.id}`} className="flex items-center gap-1 hover:text-[#6B1D2F]" data-testid={`question-comment-${q.id}`}><MessageCircle className="h-3.5 w-3.5" />{q.comments_count}</Link>
          <button onClick={share} className="flex items-center gap-1 hover:text-[#6B1D2F]" data-testid={`question-share-${q.id}`}><Share2 className="h-3.5 w-3.5" />Share</button>
        </div>
      </div>
    </div>
  );
}

export function CreatorCard({ c }) {
  const { requireAuth } = useApp();
  const [on, setOn] = useState(false);
  const follow = requireAuth(async () => { const r = await api.post(`/users/${c.id}/follow`); setOn(r.data.following); });
  return (
    <div data-testid={`creator-card-${c.id}`} className="card flex w-56 shrink-0 flex-col items-center p-5 text-center transition-transform hover:-translate-y-1">
      <Link to={`/profile/${c.id}`}><Avatar src={c.avatar} name={c.name} size="h-20 w-20" /></Link>
      <Link to={`/profile/${c.id}`} className="mt-3 font-semibold">{c.name} {c.verified && <span className="text-[#D97706]">✦</span>}</Link>
      <p className="text-xs text-[#78716C]">{c.city} · {(c.followers_count || 0).toLocaleString("en-IN")} followers</p>
      <p className="mt-2 line-clamp-2 text-xs text-[#57534E]">{c.bio}</p>
      <button onClick={follow} className={on ? "btn-ghost mt-4 !py-1.5 text-xs" : "btn-maroon mt-4 !py-1.5 text-xs"} data-testid={`creator-follow-${c.id}`}>{on ? "Following" : "Follow"}</button>
    </div>
  );
}
